import { FormEvent, useState } from 'react'
import { Trans } from '@lingui/react/macro'

import { useBooking } from '../../../model/bookingContext'
import { PatientConsent } from '../../../model/bookingTypes'
import { DataSharingConsentSection } from './sections/DataSharingConsentSection'
import { FreeChoiceConsentSection } from './sections/FreeChoiceConsentSection'
import { InterestLinksConsentSection } from './sections/InterestLinksConsentSection'
import { PricingConsentSection } from './sections/PricingConsentSection'
import { SecondaryDataConsentSection } from './sections/SecondaryDataConsentSection'
import { TeleconsultationInfoConsentSection } from './sections/TeleconsultationInfoConsentSection'

export function ConsentForm() {
  const { state, dispatch } = useBooking()

  const [consents, setConsents] = useState<PatientConsent>({
    freeChoice: state.consents?.freeChoice ?? false,
    informedAboutTeleconsultation: state.consents?.informedAboutTeleconsultation ?? false,
    acceptedPricing: state.consents?.acceptedPricing ?? false,
    understoodDataSharing: state.consents?.understoodDataSharing ?? false,
    understoodInterestLinks: state.consents?.understoodInterestLinks ?? false,
    dataForResearch: state.consents?.dataForResearch ?? false,
    dataForServiceImprovement: state.consents?.dataForServiceImprovement ?? false,
    acceptedAt: state.consents?.acceptedAt ?? null,
  })

  const updateConsent = (key: keyof PatientConsent, value: boolean) => {
    setConsents((prev) => ({ ...prev, [key]: value }))
  }

  const isFormValid =
    consents.freeChoice &&
    consents.informedAboutTeleconsultation &&
    consents.acceptedPricing &&
    consents.understoodDataSharing &&
    consents.understoodInterestLinks

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!isFormValid) return

    dispatch({
      type: 'SET_CONSENTS',
      payload: {
        ...consents,
        acceptedAt: new Date().toISOString(),
      },
      goToNextStep: true,
    })
  }

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
      <FreeChoiceConsentSection consents={consents} updateConsent={updateConsent} />
      <TeleconsultationInfoConsentSection consents={consents} updateConsent={updateConsent} />
      <PricingConsentSection consents={consents} updateConsent={updateConsent} />
      <DataSharingConsentSection consents={consents} updateConsent={updateConsent} />
      <InterestLinksConsentSection consents={consents} updateConsent={updateConsent} />
      <SecondaryDataConsentSection consents={consents} updateConsent={updateConsent} />

      <div className="flex justify-end sm:absolute sm:top-full sm:right-0 sm:!m-0">
        <button
          className="inline-flex flex-1 items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-gray-300 sm:rounded-none"
          disabled={!isFormValid}
          data-testid="consent-continue"
          type="submit"
        >
          <Trans>Next</Trans>
        </button>
      </div>
    </form>
  )
}
